// Unified applicability-target picker (DEV-16998) for binding a memory/skill.
// Extends AgentMultiSelect to offer agents, agent collections, AND workflow
// llm-agent nodes as selectable, visually-distinguished chips. A collection
// reaches every member agent (current + future); a node target scopes the
// memory/skill to one llm-agent node in a workflow graph (DEV-17157).
//
// Value is split into three sets so the caller can post `agentIds` +
// `collectionIds` + `nodeBindings` directly to the unified memory/skill
// create/edit contract. A node is the STABLE `(workflowId, nodeKey)` pair, not
// an id in the agent/collection namespace — internally it carries a synthetic
// composite id (`node|<workflowId>|<nodeKey>`) purely for the selection/dedupe
// machinery, and maps back to the pair when building the value.
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCollectionList } from '../../lib/agent-collection';
import { useAgentList } from '../../lib/agent-list';
import { useBindableWorkflowNodes } from '../../lib/workflow-node-binding';

import './AgentMultiSelect.css';

type Kind = 'agent' | 'collection' | 'node';
interface IOption {
  id: string;
  name: string;
  kind: Kind;
  /** Code-managed global system agent (DEV-16995) — badged in rows/chips. */
  managed?: boolean;
  /** Node targets only — the stable pair the value is built from. */
  workflowId?: string;
  nodeKey?: string;
}

export interface INodeTarget {
  workflowId: string;
  nodeKey: string;
}

export interface IBindingTargets {
  agentIds: string[];
  collectionIds: string[];
  nodeBindings: INodeTarget[];
}

/** Internal composite id for a node target's selection/dedupe identity. */
const nodeId = (t: INodeTarget) => `node|${t.workflowId}|${t.nodeKey}`;

export function BindingTargetSelect({
  value,
  onChange,
  placeholder,
  includeNodes = false,
}: {
  value: IBindingTargets;
  onChange: (next: IBindingTargets) => void;
  placeholder?: string;
  /**
   * Offer workflow llm-agent nodes as selectable targets (DEV-17157). Memory
   * surfaces set this; skills leave it off until their write contract carries
   * node bindings too — otherwise the picker would offer targets the caller
   * silently drops.
   */
  includeNodes?: boolean;
}) {
  // Agents include the global managed chat/refinement agents (DEV-16995).
  const agentsQuery = useAgentList({ includeSystem: true });
  const collectionsQuery = useCollectionList();
  const nodesQuery = useBindableWorkflowNodes({ enabled: includeNodes });
  const resolvedPlaceholder =
    placeholder ??
    (includeNodes
      ? 'Search agents, collections, and workflow nodes…'
      : 'Search agents and collections…');

  const options = useMemo<IOption[]>(() => {
    const agents: IOption[] = (agentsQuery.data ?? []).map((a) => ({
      id: a.id,
      name: a.name,
      kind: 'agent',
      managed: a.managed,
    }));
    const collections: IOption[] = (collectionsQuery.data ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      kind: 'collection',
    }));
    const nodes: IOption[] = includeNodes
      ? (nodesQuery.data ?? []).map((n) => ({
          id: nodeId(n),
          name: `${n.workflowName} › ${n.nodeKey}`,
          kind: 'node',
          workflowId: n.workflowId,
          nodeKey: n.nodeKey,
        }))
      : [];
    return [...collections, ...agents, ...nodes];
  }, [agentsQuery.data, collectionsQuery.data, nodesQuery.data, includeNodes]);

  const nameById = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const selectedIds = useMemo(
    () => new Set([...value.agentIds, ...value.collectionIds, ...value.nodeBindings.map(nodeId)]),
    [value.agentIds, value.collectionIds, value.nodeBindings],
  );

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((o) => !selectedIds.has(o.id))
      .filter((o) => q.length === 0 || o.name.toLowerCase().includes(q) || o.id.includes(q))
      .slice(0, 20);
  }, [options, selectedIds, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function add(o: IOption) {
    if (o.kind === 'agent') {
      onChange({ ...value, agentIds: [...value.agentIds, o.id] });
    } else if (o.kind === 'collection') {
      onChange({ ...value, collectionIds: [...value.collectionIds, o.id] });
    } else if (o.workflowId && o.nodeKey) {
      onChange({
        ...value,
        nodeBindings: [...value.nodeBindings, { workflowId: o.workflowId, nodeKey: o.nodeKey }],
      });
    }
    setQuery('');
    setActiveIndex(0);
  }

  function remove(o: IOption) {
    if (o.kind === 'agent') {
      onChange({ ...value, agentIds: value.agentIds.filter((x) => x !== o.id) });
    } else if (o.kind === 'collection') {
      onChange({ ...value, collectionIds: value.collectionIds.filter((x) => x !== o.id) });
    } else {
      onChange({ ...value, nodeBindings: value.nodeBindings.filter((n) => nodeId(n) !== o.id) });
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = suggestions[activeIndex] ?? suggestions[0];
      if (target) add(target);
    }
  }

  const chipClass: Record<Kind, string> = {
    collection: 'tag is-sky',
    agent: 'tag is-sage',
    node: 'tag is-plum',
  };
  const glyph: Record<Kind, string> = { collection: '◆ ', agent: '', node: '⬡ ' };

  const chips: IOption[] = [
    ...value.collectionIds.map(
      (id) => nameById.get(id) ?? { id, name: id, kind: 'collection' as const },
    ),
    ...value.agentIds.map((id) => nameById.get(id) ?? { id, name: id, kind: 'agent' as const }),
    ...value.nodeBindings.map(
      (n) =>
        nameById.get(nodeId(n)) ?? {
          id: nodeId(n),
          name: `${n.workflowId} › ${n.nodeKey}`,
          kind: 'node' as const,
          workflowId: n.workflowId,
          nodeKey: n.nodeKey,
        },
    ),
  ];

  const isLoading = agentsQuery.isLoading || collectionsQuery.isLoading || nodesQuery.isLoading;
  const isError = agentsQuery.isError || collectionsQuery.isError || nodesQuery.isError;
  const kindLabel: Record<Kind, string> = {
    collection: 'collection',
    agent: 'agent',
    node: 'workflow node',
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <div className="tag-input">
        {chips.map((o) => (
          <span key={`${o.kind}:${o.id}`} className={chipClass[o.kind]}>
            {glyph[o.kind]}
            {o.name}
            <button
              type="button"
              className="x"
              onClick={() => remove(o)}
              aria-label={`Remove ${o.name}`}
            >
              <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M1 1l8 8M9 1l-8 8" />
              </svg>
            </button>
          </span>
        ))}
        <input
          role="combobox"
          aria-expanded={open}
          aria-controls="binding-target-listbox"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={chips.length === 0 ? resolvedPlaceholder : ''}
        />
      </div>

      {open && (isLoading || isError || suggestions.length > 0 || query.trim().length > 0) ? (
        <div className="agent-multiselect__dropdown" role="listbox" id="binding-target-listbox">
          {isLoading ? (
            <div className="agent-multiselect__option agent-multiselect__option--empty">
              Loading…
            </div>
          ) : isError ? (
            <div
              className="agent-multiselect__option agent-multiselect__option--empty"
              style={{ color: 'var(--status-danger)' }}
            >
              Failed to load targets — reload to retry.
            </div>
          ) : suggestions.length === 0 ? (
            <div className="agent-multiselect__option agent-multiselect__option--empty">
              No agents, collections, or workflow nodes match “{query.trim()}”.
            </div>
          ) : (
            suggestions.map((o, i) => (
              <button
                key={`${o.kind}:${o.id}`}
                type="button"
                role="option"
                aria-selected={i === activeIndex}
                className={`agent-multiselect__option${i === activeIndex ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => add(o)}
              >
                <span>
                  {glyph[o.kind]}
                  {o.name}
                  {o.managed ? (
                    <span
                      className="badge badge--neutral"
                      style={{ marginLeft: 'var(--sp-1)' }}
                      title="Global managed system agent"
                    >
                      system
                    </span>
                  ) : null}
                </span>
                <span className="t-xs t-muted">{kindLabel[o.kind]}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
